import React, { useState, useContext, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  MenuItem, 
  Select, 
  InputLabel, 
  FormControl, 
  Grid,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { CONTEXTS, CONTROLLERS } from 'figshelf-modules';

const roles = [
  { value: 'user', label: 'User' },
  { value: 'moderator', label: 'Moderator' },
  { value: 'admin', label: 'Admin' }
];

const statuses = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'blocked', label: 'Suspended' }
];

const adminLevels = [
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'system_admin', label: 'System Admin' },
  { value: 'moderator', label: 'Moderator' },
  { value: 'content_manager', label: 'Content Manager' },
  { value: 'support_admin', label: 'Support Admin' },
  { value: 'finance_admin', label: 'Finance Admin' },
  { value: 'read_only_admin', label: 'Read Only Admin' }
];

const EditUserModal = ({ open, onClose, user, onUserUpdated }) => {
  const api = useContext(CONTEXTS.APIContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    username: '',
    email: '',
    phoneNumber: '',
    role: 'user',
    status: 'active',
    adminLevel: '',
    displayName: '',
    country: '',
    city: '',
    bio: '',
    dateOfBirth: '',
    pronoun: '',
    isPremium: false,
    isEmailVerified: true,
    isPhoneVerified: false
  });

  useEffect(() => {
    if (user) {
      // Format dateOfBirth properly
      let formattedDate = '';
      if (user.profile?.dateOfBirth) {
        const dob = user.profile.dateOfBirth;
        if (typeof dob === 'string') {
          formattedDate = dob.split('T')[0];
        } else if (dob instanceof Date) {
          formattedDate = dob.toISOString().split('T')[0];
        }
      }

      setForm({
        username: user.username || '',
        email: user.email || '',
        phoneNumber: user.phoneNumber || '',
        role: user.role || 'user',
        status: user.status || 'active',
        adminLevel: user.adminLevel || '',
        displayName: user.profile?.displayName || '',
        country: user.profile?.country || '',
        city: user.profile?.city || '',
        bio: user.profile?.bio || '',
        dateOfBirth: formattedDate,
        pronoun: user.profile?.pronoun || '',
        isPremium: user.isPremium || false,
        isEmailVerified: user.isEmailVerified || false,
        isPhoneVerified: user.isPhoneVerified || false
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const adminController = new CONTROLLERS.AdminController(api);
      const userData = { ...form };
      
      // Remove empty fields
      Object.keys(userData).forEach(key => {
        if (userData[key] === '' || userData[key] === null) {
          delete userData[key];
        }
      });

      await adminController.updateUser(user.id, userData);
      
      if (onUserUpdated) {
        onUserUpdated();
      }
      
      onClose();
    } catch (err) {
      console.error('Error updating user:', err);
      setError(err.response?.data?.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit User</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Username" 
                name="username" 
                value={form.username} 
                onChange={handleChange} 
                fullWidth 
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Email" 
                name="email" 
                type="email"
                value={form.email} 
                onChange={handleChange} 
                fullWidth 
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Phone Number" 
                name="phoneNumber" 
                value={form.phoneNumber} 
                onChange={handleChange} 
                fullWidth 
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select name="role" value={form.role} onChange={handleChange} label="Role">
                  {roles.map((role) => (
                    <MenuItem key={role.value} value={role.value}>{role.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select name="status" value={form.status} onChange={handleChange} label="Status">
                  {statuses.map((status) => (
                    <MenuItem key={status.value} value={status.value}>{status.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {(form.role === 'admin' || form.role === 'moderator') && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Admin Level</InputLabel>
                  <Select name="adminLevel" value={form.adminLevel} onChange={handleChange} label="Admin Level">
                    {adminLevels.map((level) => (
                      <MenuItem key={level.value} value={level.value}>{level.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Display Name" 
                name="displayName" 
                value={form.displayName} 
                onChange={handleChange} 
                fullWidth 
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Country" 
                name="country" 
                value={form.country} 
                onChange={handleChange} 
                fullWidth 
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="City" 
                name="city" 
                value={form.city} 
                onChange={handleChange} 
                fullWidth 
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Date of Birth" 
                name="dateOfBirth" 
                type="date"
                value={form.dateOfBirth} 
                onChange={handleChange} 
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Pronoun" 
                name="pronoun" 
                value={form.pronoun} 
                onChange={handleChange} 
                fullWidth
                placeholder="e.g., He/Him, She/Her, They/Them"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField 
                label="Bio" 
                name="bio" 
                value={form.bio} 
                onChange={handleChange} 
                fullWidth
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Checkbox 
                    name="isPremium" 
                    checked={form.isPremium} 
                    onChange={handleChange} 
                  />
                }
                label="Premium User"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Checkbox 
                    name="isEmailVerified" 
                    checked={form.isEmailVerified} 
                    onChange={handleChange} 
                  />
                }
                label="Email Verified"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Checkbox 
                    name="isPhoneVerified" 
                    checked={form.isPhoneVerified} 
                    onChange={handleChange} 
                  />
                }
                label="Phone Verified"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>Cancel</Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading ? 'Updating...' : 'Update User'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditUserModal;
